import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, X } from 'lucide-react';

const tierInfo = {
  seedling: {
    name: 'Seedling',
    emoji: '🌱',
    price: '$4.99/mo',
    color: 'emerald',
    features: ['Spiritual features', 'Role assignments', 'Album sharing', 'Email notifications']
  },
  growing: {
    name: 'Growing',
    emoji: '🌿',
    price: '$9.99/mo',
    color: 'blue',
    features: ['Dues management', 'Voting & governance', 'Album downloads', 'Priority messages']
  },
  flourishing: {
    name: 'Flourishing',
    emoji: '🌳',
    price: '$14.99/mo',
    color: 'amber',
    features: ['Professional resources', 'Family analytics', 'Data export', 'Priority support']
  }
};

const featureToTier = {
  // Seedling features
  spiritual_features: 'seedling',
  prayer_requests: 'seedling',
  devotionals: 'seedling',
  assign_roles: 'seedling',
  edit_timeline: 'seedling',
  email_notifications: 'seedling',
  invite_proxy_members: 'seedling',
  share_albums: 'seedling',
  event_tasks: 'seedling',
  
  // Growing features
  dues_management: 'growing',
  voting_governance: 'growing',
  share_albums_download: 'growing',
  event_budget: 'growing',
  priority_messages: 'growing',
  
  // Flourishing features
  resources_professionals: 'flourishing',
  family_analytics: 'flourishing',
  achievements_badges: 'flourishing',
  export_data: 'flourishing',
  invitation_approval: 'flourishing',
};

const featureNames = {
  spiritual_features: 'Spiritual Features',
  prayer_requests: 'Prayer Requests',
  devotionals: 'Devotionals',
  assign_roles: 'Role Assignments',
  edit_timeline: 'Timeline Editing',
  email_notifications: 'Email Notifications',
  invite_proxy_members: 'Member Invitations',
  share_albums: 'Album Sharing',
  event_tasks: 'Event Tasks',
  dues_management: 'Dues Management',
  voting_governance: 'Voting & Governance',
  share_albums_download: 'Album Downloads',
  event_budget: 'Event Budgets',
  priority_messages: 'Priority Messages',
  resources_professionals: 'Professional Resources',
  family_analytics: 'Family Analytics',
  achievements_badges: 'Achievements & Badges',
  export_data: 'Data Export',
  invitation_approval: 'Invitation Approval',
};

export default function UpgradeModal({ isOpen, onClose, feature, requiredTier }) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  // Use requiredTier from API if it's a paid tier, otherwise fall back to featureToTier mapping
  const tier = (requiredTier && requiredTier !== 'free') 
    ? requiredTier 
    : (featureToTier[feature] || 'seedling');
  const info = tierInfo[tier] || tierInfo.seedling; // Fallback to seedling if tier not found
  const featureName = featureNames[feature] || feature || 'Feature';
  
  const handleUpgrade = () => {
    onClose();
    navigate('/subscription');
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
            tier === 'seedling' ? 'bg-emerald-100' :
            tier === 'growing' ? 'bg-blue-100' :
            'bg-amber-100'
          }`}>
            <Lock className={`w-8 h-8 ${
              tier === 'seedling' ? 'text-emerald-600' :
              tier === 'growing' ? 'text-blue-600' :
              'text-amber-600'
            }`} />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Unlock {featureName}
          </h2>
          <p className="text-gray-600">
            This feature is available on the <strong>{info.name}</strong> plan and above
          </p>
        </div>
        
        <div className={`rounded-xl p-4 mb-6 ${
          tier === 'seedling' ? 'bg-emerald-50 border border-emerald-200' :
          tier === 'growing' ? 'bg-blue-50 border border-blue-200' :
          'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl">{info.emoji}</span>
            <div>
              <h3 className="font-bold text-gray-900">{info.name}</h3>
              <p className={`text-sm ${
                tier === 'seedling' ? 'text-emerald-600' :
                tier === 'growing' ? 'text-blue-600' :
                'text-amber-600'
              }`}>{info.price}</p>
            </div>
          </div>
          
          <ul className="space-y-2">
            {info.features.map((feat, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                <Sparkles className={`w-4 h-4 ${
                  tier === 'seedling' ? 'text-emerald-500' :
                  tier === 'growing' ? 'text-blue-500' :
                  'text-amber-500'
                }`} />
                {feat}
              </li>
            ))}
          </ul>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            Maybe Later
          </button>
          <button
            onClick={handleUpgrade}
            className={`flex-1 py-3 text-white rounded-lg font-medium ${
              tier === 'seedling' ? 'bg-emerald-600 hover:bg-emerald-700' :
              tier === 'growing' ? 'bg-blue-600 hover:bg-blue-700' :
              'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            View Plans
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook for easy feature gating
export function useFeatureGate() {
  const [modalState, setModalState] = React.useState({
    isOpen: false,
    feature: null,
    requiredTier: null
  });
  
  const checkFeature = React.useCallback(async (feature, userFeatures) => {
    if (!userFeatures?.available?.[feature]) {
      const requiredTier = userFeatures?.locked?.[feature] || featureToTier[feature];
      setModalState({
        isOpen: true,
        feature,
        requiredTier
      });
      return false;
    }
    return true;
  }, []);
  
  const closeModal = React.useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);
  
  return {
    modalState,
    checkFeature,
    closeModal,
    UpgradeModalComponent: (
      <UpgradeModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        feature={modalState.feature}
        requiredTier={modalState.requiredTier}
      />
    )
  };
}
